import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent";
import { styled, keyframes } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import eLearn from "../assets/eLearn.png";
import softDev from "../assets/softDev.jpg";
import staffAug from "../assets/staffAug.jpg";
import webDev from "../assets/webDev.jpg";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const services = [
  {
    title: "IT & Software Outsourcing",
    image: softDev,
    shortDescription:
      "Innovative IT and software solutions tailored to your business needs.",
    detailedDescription:
      "Our IT & Software Outsourcing service provides end-to-end development solutions, from planning and design to deployment and maintenance. We specialize in custom software that addresses your unique business challenges, leveraging advanced technologies and agile methodologies to ensure scalability, efficiency, and innovation.\n\n" +
      "Our experienced team collaborates closely with you to understand your vision and transform it into a robust digital solution. Whether you need enterprise applications, mobile apps, or cloud-based platforms, we offer expertise across diverse technologies and frameworks.\n\n" +
      "Post-launch, we continue to provide support and enhancements, ensuring your software remains competitive and high-performing. Discover how our solutions have empowered businesses through our client success stories.",
  },
  {
    title: "Human Resource and Business Process Outsourcing",
    image: webDev,
    shortDescription:
      "Streamlined HR and business process solutions to optimize your operations.",
    detailedDescription:
      "Our Human Resource and Business Process Outsourcing (BPO) services enable businesses to focus on their core operations while we handle their HR and administrative needs. From talent acquisition to payroll management and compliance, we ensure your business runs smoothly and efficiently.\n\n" +
      "Our BPO services also cover customer support, data processing, and back-office management, tailored to meet the unique needs of your organization. We bring strategic expertise and operational excellence to every partnership.\n\n" +
      "Through our flexible and cost-effective outsourcing solutions, companies enhance productivity and scalability. Explore our success stories to see how we’ve driven operational efficiency for businesses worldwide.",
  },
  {
    title: "Digital Marketing & Content Services",
    image: eLearn,
    shortDescription:
      "Creative digital marketing strategies and content that drive engagement.",
    detailedDescription:
      "Our Digital Marketing & Content Services help brands establish a powerful online presence and connect with their audience. From SEO and social media campaigns to content creation and branding, we craft strategies tailored to your goals.\n\n" +
      "Our content services include blog writing, video production, and visual design, ensuring your message resonates across channels. We use data-driven insights to optimize performance and deliver measurable results.\n\n" +
      "Whether launching a new product or strengthening your market position, our digital marketing experts drive growth and engagement. Learn how our innovative campaigns have transformed brands.",
  },
  {
    title: "Healthcare & Medical Outsourcing",
    image: staffAug,
    shortDescription:
      "Comprehensive healthcare outsourcing for enhanced patient care and efficiency.",
    detailedDescription:
      "Our Healthcare & Medical Outsourcing services provide medical institutions with skilled professionals and streamlined processes, ensuring high-quality patient care and operational efficiency. We offer solutions in medical billing, coding, telehealth support, and administrative management.\n\n" +
      "With a deep understanding of healthcare compliance and patient confidentiality, our team ensures the highest standards in every service we provide. From staffing support to medical transcription, our solutions enhance productivity and accuracy.\n\n" +
      "Partnering with us means gaining access to specialized expertise and innovative practices that improve patient outcomes and reduce costs. See how our outsourcing services have made a difference in the healthcare sector.",
  },
];

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: "100%",
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  animation: `${fadeInUp} 0.8s ease-out forwards`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[10],
  },
}));

const ImageBox = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 180,
  borderRadius: theme.shape.borderRadius,
  backgroundSize: "cover",
  backgroundPosition: "center",
}));

export default function Services() {
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState(null);

  const handleOpenModal = (service) => {
    setSelectedService(service);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedService(null);
  };

  return (
    <Container sx={{ textAlign: "center", py: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Our Services
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Explore our range of services designed to drive innovation and growth.
      </Typography>
      <Grid container spacing={4}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={service.title}>
            <StyledCard onClick={() => handleOpenModal(service)}>
              <ImageBox sx={{ backgroundImage: `url(${service.image})` }} />
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {service.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {service.shortDescription}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(service);
                  }}
                >
                  Learn More
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Full-screen modal for detailed service information */}
      <Dialog fullScreen open={openModal} onClose={handleCloseModal}>
        {selectedService && (
          <>
            <AppBar sx={{ position: "relative", background: "#115097" }}>
              <Toolbar sx={{ justifyContent: "center", position: "relative" }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleCloseModal}
                  aria-label="close"
                  sx={{ position: "absolute", left: 8 }}
                >
                  <CloseRoundedIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ textAlign: "center" }}
                >
                  {selectedService.title}
                </Typography>
                <Button
                  autoFocus
                  color="inherit"
                  onClick={handleCloseModal}
                  sx={{ position: "absolute", right: 8 }}
                >
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            <DialogContent
              dividers
              sx={{
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body1"
                style={{ maxWidth: "1200px" }}
                paragraph
              >
                {selectedService.detailedDescription.split("\n\n")[0]}
              </Typography>
              <Box
                sx={{
                  my: 3,
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <img
                  src={selectedService.image}
                  alt={selectedService.title}
                  style={{ width: "40%", borderRadius: 8 }}
                />
              </Box>
              <Typography
                variant="body1"
                style={{ maxWidth: "1200px" }}
                paragraph
              >
                {selectedService.detailedDescription.split("\n\n")[1]}
              </Typography>
              <Typography
                variant="body1"
                style={{ maxWidth: "1200px" }}
                paragraph
              >
                {selectedService.detailedDescription.split("\n\n")[2]}
              </Typography>
              <Typography
                variant="body1"
                style={{ maxWidth: "1200px" }}
                paragraph
              >
                Additional content, case studies, client testimonials, and
                detailed explanations of our processes can be added here. This
                section can include multiple images, interactive elements, and
                even embedded videos to give you a full picture of the services
                we provide.
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}
